<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useQuotesStore } from 'dashboard/stores/quotes';

import QuotesListLayout from 'dashboard/components-next/CRM/QuotesListLayout/QuotesListLayout.vue';
import QuoteCard from 'dashboard/components-next/CRM/QuoteCard/QuoteCard.vue';
import QuotesEmptyState from 'dashboard/components-next/CRM/QuotesEmptyState/QuotesEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateQuoteDialog from 'dashboard/components-next/CRM/CreateQuoteDialog/CreateQuoteDialog.vue';

const DEBOUNCE_DELAY = 300;

const quotesStore = useQuotesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const quotes = computed(() => quotesStore.getQuotes);
const uiFlags = computed(() => quotesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const sortQuery = computed(() => route.query?.sort || 'updated_at_desc');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const isFetchingList = computed(() => uiFlags.value.fetchingList);

const hasQuotes = computed(() => quotes.value.length > 0);
const isQuoteIndexView = computed(
  () => route.name === 'crm_quotes_index' && pageNumber.value === 1
);

const showEmptyStateLayout = computed(() => {
  return !searchQuery.value && !hasQuotes.value && isQuoteIndexView.value;
});

const showEmptyText = computed(() => {
  return (searchQuery.value || !isQuoteIndexView.value) && !hasQuotes.value;
});

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchQuotes = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortQuery.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  await quotesStore.get({
    page: currentPage,
    search: currentSearch,
    sort: currentSort,
  });
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchQuotes(1, query, sortQuery.value);
}, DEBOUNCE_DELAY);

const onSortChange = sort => {
  fetchQuotes(pageNumber.value, searchQuery.value, sort);
};

const onPageChange = page => {
  fetchQuotes(page, searchValue.value, sortQuery.value);
};

const handleShowQuote = quoteId => {
  router.push(`/app/accounts/${route.params.accountId}/crm/quotes/${quoteId}`);
};

const createQuoteDialogRef = ref(null);

const handleAddQuote = () => {
  createQuoteDialogRef.value?.open();
};

const handleCreateQuote = async () => {
  useAlert(t('QUOTES.CREATE_SUCCESS'));
  fetchQuotes();
};

const emptyStateMessage = computed(() => {
  if (!searchQuery.value) {
    return t('QUOTES.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  }
  return t('QUOTES.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchQuotes();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <QuotesListLayout
      :search-value="searchValue"
      :header-title="$t('QUOTES.TITLE')"
      :current-page="pageNumber"
      :total-items="quotes.length"
      @page-change="onPageChange"
      @search="onSearch"
      @add="handleAddQuote"
    >
      <template #empty>
        <div
          v-if="isFetchingList"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <QuotesEmptyState
          v-else-if="showEmptyStateLayout"
          class="pt-14"
          @create="handleAddQuote"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>
      </template>

      <div v-if="!isFetchingList && hasQuotes" class="flex flex-col gap-4 pt-4 pb-6">
        <QuoteCard
          v-for="quote in quotes"
          :key="quote.id"
          :id="quote.id"
          :quote-number="quote.quoteNumber"
          :title="quote.title"
          :total-amount="quote.totalAmount"
          :currency="quote.currency"
          :status="quote.status"
          :valid-until="quote.validUntil"
          :updated-at="quote.updatedAt"
          :company-name="quote.company?.name"
          :contact-name="quote.contact?.name"
          :user-name="quote.user?.name"
          @show-quote="handleShowQuote"
        />
      </div>
    </QuotesListLayout>
    
    <CreateQuoteDialog
      ref="createQuoteDialogRef"
      @create="handleCreateQuote"
    />
  </div>
</template>
